import { useEffect, useMemo, useState } from 'react';
import { Locale, translate } from './translations';

const STORAGE_KEY = 'bytla_locale';

const isLocale = (value: string): value is Locale =>
  value === 'en' || value === 'th' || value === 'id';

const getCookieLocale = (): Locale | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )bytla_locale=([^;]+)/);
  const value = match ? decodeURIComponent(match[1]) : null;
  return value && isLocale(value) ? value : null;
};

export function useLocale() {
  const [locale, setLocaleState] = useState<Locale>('en');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cookieLocale = getCookieLocale();
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const storedLocale = stored && isLocale(stored) ? stored : null;
    const nextLocale = cookieLocale || storedLocale;

    if (nextLocale) {
      setLocaleState(nextLocale);
      window.localStorage.setItem(STORAGE_KEY, nextLocale);
      document.cookie = `bytla_locale=${nextLocale}; path=/; max-age=31536000`;
      document.documentElement.lang = nextLocale;
    }
  }, []);

  const setLocale = (next: Locale) => {
    setLocaleState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
      document.cookie = `bytla_locale=${next}; path=/; max-age=31536000`;
      document.documentElement.lang = next;
      window.location.reload();
    }
  };

  const t = useMemo(() => (key: string) => translate(locale, key), [locale]);

  return { locale, setLocale, t };
}
